import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { isAdmin } from '@/constants/roles';
import { NewInspection } from './NewInspection';
import { InspectionHistory } from './InspectionHistory';
import { InspectionTemplates } from './InspectionTemplates';
import { InspectionReport } from './InspectionReport';
import { RunInspection } from './RunInspection';
import { InspectionAnalytics } from './InspectionAnalytics';
import { History, FileStack, Play, BarChart3 } from 'lucide-react';

interface InspectionsModuleProps {
  onBack?: () => void;
}

export const InspectionsModule: React.FC<InspectionsModuleProps> = ({ onBack }) => {
  const { userRole } = useAuth();
  const canManageTemplates = isAdmin(userRole as any);
  
  const [activeTab, setActiveTab] = useState('new');
  const [runningInspectionId, setRunningInspectionId] = useState<string | null>(null);
  const [viewingReportId, setViewingReportId] = useState<string | null>(null);

  // If running an inspection, show the run inspection view
  if (runningInspectionId) {
    return (
      <RunInspection 
        inspectionId={runningInspectionId}
        onBack={() => setRunningInspectionId(null)}
        onSubmit={() => {
          setRunningInspectionId(null);
          setActiveTab('history');
        }}
      />
    );
  }

  // If viewing a report, show the report view
  if (viewingReportId) {
    return (
      <InspectionReport
        inspectionId={viewingReportId}
        onBack={() => setViewingReportId(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Inspections</h2>
        <p className="text-sm text-muted-foreground">
          Manage facility inspections, checklists, and compliance reports
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="new" className="flex items-center gap-2">
            <Play className="h-4 w-4" />
            <span className="hidden sm:inline">New Inspection</span>
            <span className="sm:hidden">New</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">History</span>
            <span className="sm:hidden">History</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Analytics</span>
            <span className="sm:hidden">Stats</span>
          </TabsTrigger>
          {canManageTemplates && (
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileStack className="h-4 w-4" />
              <span className="hidden sm:inline">Templates</span>
              <span className="sm:hidden">Templates</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="new" className="mt-6">
          <NewInspection 
            onStartInspection={(inspectionId) => setRunningInspectionId(inspectionId)}
            onResumeInspection={(inspectionId) => setRunningInspectionId(inspectionId)}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <InspectionHistory 
            onViewReport={(inspectionId) => setViewingReportId(inspectionId)}
            onResumeInspection={(inspectionId) => setRunningInspectionId(inspectionId)}
          />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <InspectionAnalytics />
        </TabsContent>

        {canManageTemplates && (
          <TabsContent value="templates" className="mt-6">
            <InspectionTemplates />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
